import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageContent, { PageBreadcrumbProps } from "../PageContent";
import CTEyeRootSubmenu from "./CTEyeRootSubmenu";
import CTEyeService from "../../services/CTEyeService";
import { UserService } from "../../services";
import { Roles } from "../../models";
import { CTEyeClientSummaryModel, LiveInspectionResponse } from "../../data/entities";
import { getYesterday } from "../../app/dateHelpers";
import { POLL_LIVE_DETECTION_MS } from "./helper";
import { DashboardStatCard, LiveDevice, LiveInspectionsBanner } from "./components";
import { AgentsGrid, RecentInspectionsGrid, RecentFilesGrid } from "./components/dashboard";

export function CTEyeDashboard() {
  const title = "CTEye Dashboard";
  const breadcrumbs: PageBreadcrumbProps = { showHome: true, values: [{ label: title }] };
  const submenus = <CTEyeRootSubmenu />;
  const navigate = useNavigate();

  const isStaffOrAdmin = UserService.isInAnyRole([Roles.admin, Roles.staff, Roles.dataAnalyst]);
  const companyName = isStaffOrAdmin ? "All Companies" : UserService.getCompanyName();

  const [clientSummaries, setClientSummaries] = useState<CTEyeClientSummaryModel[]>([]);
  const [liveInspections, setLiveInspections] = useState<LiveInspectionResponse[]>([]);

  const ctEyeService = useMemo(() => new CTEyeService(), []);

  const liveDevices: LiveDevice[] = liveInspections.map(i => ({
    clientId: i.clientId,
    clientName: i.clientName,
    companyName: i.companyName,
    fileName: i.fileName,
    path: i.filePath,
  }));

  useEffect(() => {
    ctEyeService.getClientsSummariesOData().then((res) => {
      setClientSummaries(res.value);
    });
  }, []);

  useEffect(() => {
    const load = () => ctEyeService.getLiveInspections().then(setLiveInspections);
    load();
    const interval = setInterval(load, POLL_LIVE_DETECTION_MS);
    return () => clearInterval(interval);
  }, []);

  const totalInspections = clientSummaries.reduce((s, c) => s + c.inspectionsUsedThisMonth, 0);
  const totalReports = clientSummaries.reduce((s, c) => s + c.reportsUsedThisMonth, 0);
  const activeDeviceCount = clientSummaries.filter((c) => c.lastSeenDate != null && new Date(c.lastSeenDate) >= getYesterday()).length;

  const handleWatchLiveFromBanner = (device: LiveDevice) => {
    const params = new URLSearchParams({ filePath: device.path });
    navigate(`/cteye/clients/${device.clientId}/live-view?${params.toString()}`);
  };

  return (
    <PageContent title={title} breadcrumbs={breadcrumbs} submenu={submenus}>
      <LiveInspectionsBanner devices={liveDevices} onWatchLive={handleWatchLiveFromBanner} />

      <div className="row mb-4 g-3">
        <div className="col-12 col-sm-6 col-lg">
          <DashboardStatCard
            label="Total Agents" 
            value={clientSummaries.length} 
            subtitle={companyName} 
            accentColor="#6366f1" 
          />
        </div>
        <div className="col-12 col-sm-6 col-lg">
          <DashboardStatCard 
            label="Active Agents" 
            value={activeDeviceCount}
            subtitle="Active in last 24hr" 
            accentColor="#10b981" 
          />
        </div>
        <div className="col-12 col-sm-6 col-lg">
          <DashboardStatCard 
            label="Inspections This Month" 
            value={totalInspections} 
            subtitle={isStaffOrAdmin ? "Across all Agents" : companyName} 
            accentColor="#3b82f6" 
          />
        </div>
        <div className="col-12 col-sm-6 col-lg">
          <DashboardStatCard 
            label="Reports This Month" 
            value={totalReports} 
            subtitle={isStaffOrAdmin ? "Across all Agents" : companyName} 
            accentColor="#8b5cf6" 
          />
        </div>
      </div>

      <AgentsGrid companyName={companyName} />

      <div className="row">
        <div className="col-lg-6 mb-4">
          <RecentInspectionsGrid />
        </div>
        <div className="col-lg-6 mb-4">
          <RecentFilesGrid liveInspections={liveInspections} />
        </div>
      </div>
    </PageContent>
  );
}

export default CTEyeDashboard;